<template>
  <div class="registration">
    <form v-if="!pageLoading" @submit.prevent="handleSubmit">
      <p v-if="!employeeCode">
        <translate class="inherit-font">Please browse to the</translate>
        <span>&nbsp;</span>

        <router-link :to="{ name: 'Verification' }" v-translate>verification page</router-link>
        <span>&nbsp;</span>

        <translate class="inherit-font">to sign up.</translate>
      </p>
      <div v-else>
        <h1 :style="{ fontWeight: '600', textAlign: 'center' }">
          <span class="inherit-font" v-translate>Join the</span>
          <br />
          <span class="inherit-font" v-translate>Grant Circle</span>
        </h1>
        <div>
          <StepProgressBar :step1="true" />
        </div>
        <div>
          <h4 v-translate>Personal Information</h4>
          <md-field :class="{ 'md-invalid': this.errors }">
            <label v-translate>First name</label>
            <md-input
              name="fname"
              autocomplete="given-name"
              tabindex="1"
              required
              v-model="firstName"
            ></md-input>
            <span v-if="firstName === ''" :class="['md-error', 'ruby-font']" v-translate>
              First name may not be blank
            </span>
          </md-field>
          <md-field :class="{ 'md-invalid': this.errors }">
            <label v-translate>Last Name</label>
            <md-input
              name="lname"
              autocomplete="family-name"
              tabindex="2"
              required
              v-model="lastName"
            ></md-input>
            <span v-if="lastName === ''" :class="['md-error', 'ruby-font']" v-translate
              >Last name may not be blank</span
            >
          </md-field>
          <md-field :class="{ 'md-invalid': this.errors }">
            <label v-translate>Personal email address</label>
            <md-input
              name="email"
              autocomplete="email"
              tabindex="3"
              required
              v-model="email"
            ></md-input>
            <span :class="['md-error', 'ruby-font']">
              {{ errors && errors.email ? errors.email[0] : '' }}
            </span>
          </md-field>
          <md-field :class="{ 'md-invalid': this.errors || !this.emailsMatch }">
            <label v-translate>Re-enter personal email address</label>
            <md-input
              name="emailC"
              autocomplete="email"
              tabindex="3"
              required
              v-model="confirmEmail"
            ></md-input>
            <span :class="['md-error', 'ruby-font']">
              {{ email.length && !this.emailsMatch ? $gettext('Must match') : '' }}
            </span>
          </md-field>
          <span v-translate>
            Passwords must meet the following requirements: contain at least 10 characters, with at
            least 1 number, 1 lower case letter, 1 upper case letter, and 1 special character, such
            as [~!@#$%^&amp;*()_+{}\":;'[].
          </span>
          <md-field :class="{ 'md-invalid': this.isNotValidPassword }" style="margin-bottom: 42px">
            <label :class="{ 'ruby-font': this.isNotValidPassword }" v-translate>Password</label>
            <md-input
              tabindex="4"
              type="password"
              required
              v-model="password"
              name="password"
              autocomplete="new-password"
            ></md-input>
            <!-- <span :class="['md-error', 'ruby-font']">
              {{ errors && errors.password && !password.length ? errors.password[0] : '' }}
            </span> -->
            <span class="md-error ruby-font" v-translate
              >Your password does not meet the password requirements outlined above.</span
            >
          </md-field>
          <md-field :class="{ 'md-invalid': this.errors || !this.passwordsMatch }">
            <label v-translate>Re-enter password</label>
            <md-input
              autocomplete="new-password"
              tabindex="5"
              type="password"
              required
              v-model="verifiedPassword"
            ></md-input>
            <span :class="['md-error', 'ruby-font']">
              {{ password.length && !this.passwordsMatch ? $gettext('Must match') : '' }}
            </span>
          </md-field>
          <md-field :class="{ 'md-invalid': this.errors || !this.birthYearValid }">
            <label v-translate>Year of Birth</label>
            <md-input
              tabindex="6"
              type="number"
              min="1900"
              max="2020"
              :required="employeeSchema.hasOwnProperty('birthYear')"
              v-mask="'####'"
              v-model="birthYear"
            ></md-input>
            <span :class="['md-error', 'ruby-font']">
              {{ birthYear > 0 && !this.birthYearValid ? $gettext('Birth year invalid') : '' }}
            </span>
          </md-field>
          <md-field
            :style="{ marginBottom: '40px' }"
            :class="{ 'md-invalid': this.errors || !this.zipCodeValid }"
          >
            <label v-translate>Zip Code</label>
            <md-input
              name="zip"
              autocomplete="postal-code"
              tabindex="7"
              :required="employeeSchema.hasOwnProperty('zipCode')"
              v-mask="'#####'"
              v-model="zipCode"
            ></md-input>
            <span :class="['md-error', 'ruby-font']">
              {{
                this.zipCodeValid
                  ? ''
                  : (errors && errors.zipCode && errors.zipCode[0]) ||
                    (clientErrors.zipCode && clientErrors.zipCode[0]) ||
                    $gettext('Invalid')
              }}
            </span>
          </md-field>
          <div>
            <span :class="['md-error', 'ruby-font']">
              {{ errors && errors.nonFieldErrors ? errors.nonFieldErrors[0] : '' }}
            </span>
          </div>
        </div>
        <div
          :class="{ 'md-invalid': this.errors || !this.preferredLanguage }"
          v-if="enableLangPref"
          :style="{ 'margin-bottom': '1em' }"
        >
          <p v-translate>What is your preferred language for Grant Circle communication?</p>
          <div class="checkboxes">
            <md-checkbox v-model="preferredLanguage" value="en_US">
              <p v-translate>English</p>
            </md-checkbox>
            <md-checkbox v-model="preferredLanguage" value="es_ES"
              ><p v-translate>Spanish</p></md-checkbox
            >
          </div>
          <span :class="['md-error', 'ruby-font']">
            {{ this.preferredLanguage ? '' : $gettext('Please select your preferred language') }}
          </span>
        </div>
        <div
          :class="{
            'md-invalid': this.errors || (!this.enableSmsAlerts && !this.enableEmailAlerts),
          }"
          v-if="enableSms"
          class="registration__section"
        >
          <label for="notifications">
            <p class="subheading" v-translate>Notification preferences</p>
          </label>
          <p class="registration__subtitle">
            <span class="registration__subtitle" v-translate
              >Do you prefer that we communicate with you via email, text message, or both? Select
              one or more below.</span
            >
            <span>&nbsp;</span>
            <span class="italics light" v-translate>(Messaging rates may apply)</span>
          </p>
          <div class="checkboxes">
            <md-checkbox v-model="enableEmailAlerts">
              <p v-translate>Email</p>
            </md-checkbox>
            <md-checkbox v-model="enableSmsAlerts">
              <p v-translate>SMS</p>
            </md-checkbox>
          </div>
          <span :class="['md-error', 'ruby-font']">
            {{
              this.enableSmsAlerts || this.enableEmailAlerts
                ? ''
                : $gettext('Please select at least one of the options available')
            }}
          </span>
        </div>
        <md-field
          v-if="enableSms"
          class="registration__phone-number"
          :class="{ 'md-invalid': this.errors || !this.phoneNumberValid }"
        >
          <label for="phone" v-translate>Phone number</label>
          <md-input
            tabindex="8"
            v-model="phoneNumber"
            name="phone"
            autocomplete="tel"
            v-mask="'(###)###-####'"
            id="phone"
            :required="enablePhoneContact || enableSmsAlerts"
          ></md-input>
          <span :class="['md-error', 'ruby-font']">
            {{
              (errors && errors.phoneNumber && errors.phoneNumber[0]) ||
              (clientErrors.phoneNumber && clientErrors.phoneNumber[0])
            }}
          </span>
        </md-field>
        <div v-if="enableSms" class="registration__section">
          <div :style="{ 'margin-top': '1em' }">
            <label for="contact">
              <span v-translate class="registration__subtitle"
                >Can we reach out to you by phone if we need more information from you to process
                your grant request?</span
              >
            </label>
          </div>
          <div class="checkboxes">
            <md-checkbox v-model="enablePhoneContact" :value="true">
              <p v-translate>Yes</p>
            </md-checkbox>
            <md-checkbox v-model="enablePhoneContact" :value="false">
              <p v-translate>No</p>
            </md-checkbox>
          </div>
        </div>
        <div :style="{ width: '100%' }">
          <div :style="{ display: 'flex' }">
            <label class="checkbox-container">
              <input tabindex="9" type="checkbox" id="checkbox" v-model="checked" />
              <span class="checkmark"></span>
            </label>
            <label for="checkbox" :style="{ lineHeight: '1.2' }">
              <p>
                <span class="inherit-font" v-translate>I agree to the</span>
                <span class="inherit-font">&nbsp;</span>
                <a :href="tcLink" target="_blank" v-translate
                  >Grant Circle Terms and Conditions and Data Sharing Policy.</a
                >
              </p>
            </label>
          </div>
          <div v-if="checked !== true" :class="['ruby-font']">
            * <span>{{ $gettext('Required') }}</span>
          </div>
        </div>

        <div class="button-container">
          <md-button type="submit" tabindex="9" class="next-button" :style="organizationCssVars">
            <span v-if="!loading" key="next" v-translate>Next</span>
            <span v-else-if="loading" key="spinner">
              <LoadSpinner :color="'white'" />
            </span>
          </md-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LoadSpinner from '../components/LoadSpinner'
import { objectToCamelCase } from '../services/utils'
import Employee from '../services/employees'
import StepProgressBar from '../components/stepper/StepProgressBar'
import { analyticsMixin } from '@/services/googleAnalytics'
import FAQ from '@/services/faqs'

export default {
  name: 'Registration',
  mixins: [analyticsMixin],
  components: {
    StepProgressBar,
    LoadSpinner,
  },
  data() {
    return {
      employeeCode: null,
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      password: '',
      verifiedPassword: '',
      birthYear: '',
      zipCode: '',
      checked: false,
      errors: null,
      loading: false,
      pageLoading: false,
      enableLangPref: this.$store.state.organizationDetails.enableLanguagePref,
      enableSms: true,
      enableSmsAlerts: false,
      enableEmailAlerts: true,
      enablePhoneContact: false,
      phoneNumber: '',
      preferredLanguage: this.$language.current,
      clientErrors: {},
      employeeSchema: null,
      tcLink: null,
    }
  },
  async created() {
    this.pageLoading = true
    this.employeeCode = this.$route.params.code

    /* If a legacy user was sent up from the API, pre-populate that info into our form */
    if (this.employeeCode && this.employeeCode.legacyUser) {
      const legacy = this.employeeCode.legacyUser
      this.firstName = legacy.firstName
      this.lastName = legacy.lastName
      this.email = legacy.email
      this.birthYear = legacy.birthYear
    }
    this.getEmployeeSchema()
    FAQ.api.getTcOrTaxFaq(this.organizationDetails.name, 'tc').then((faq) => {
      this.tcLink = `/faq#${faq.faqOrder}`
    })
    this.pageLoading = false
  },
  methods: {
    async getEmployeeSchema() {
      await Employee.api.schema().then((res) => {
        this.employeeSchema = res['actions']['POST']
      })
    },

    async createEmployeeAccount({
      firstName,
      lastName,
      email,
      confirmEmail,
      password,
      birthYear,
      zipCode,
      code,
      preferredLanguage,
      enableSmsAlerts,
      enableEmailAlerts,
      enablePhoneContact,
      phoneNumber,
    }) {
      this.loading = true
      this.errors = null
      const hasSetNotifPreferences = enableSmsAlerts || enableEmailAlerts
      this.trackClickedCTA()
      await Employee.api
        .registerEmployee(
          firstName,
          lastName,
          email,
          confirmEmail,
          password,
          birthYear || null,
          zipCode,
          code,
          preferredLanguage,
          enableSmsAlerts,
          enableEmailAlerts,
          enablePhoneContact,
          hasSetNotifPreferences,
          phoneNumber,
        )
        .then((res) => {
          const employee = Employee.fromAPI(res.data)
          this.$store.dispatch('updateUserToken', res.data.token)
          this.$store.dispatch('updateUser', employee)
          this.$store.commit('UPDATE_CURRENT_LANGUAGE', employee.preferredLanguage)
        })
        .catch((err) => {
          this.eventsTracker('personal-information-submission', err.response.data)
          this.errors = objectToCamelCase(err.response.data)
        })
      this.loading = false
      if (this.errors === null && this.verifiedPassword !== '') {
        this.navigateToSurveys()
      }
    },
    navigateToSurveys() {
      if (this.phoneNumber.length && this.enableSmsAlerts) {
        this.$router.push({
          name: 'Validation',
          params: {
            routeName: this.nextRoute(),
          },
        })
      }
      if (!this.phoneNumber.length || !this.enableSmsAlerts) {
        this.$router.push({
          name: this.nextRoute(),
        })
      }
    },
    nextRoute() {
      if (this.organizationDetails.isHouseholdDetailsSurveyEnabled) {
        return 'HouseholdDetailsOnboarding'
      }

      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        this.organizationDetails.isFinancialHealthSurveyEnabled
      ) {
        return 'FinancialHealthOnboarding'
      }

      if (
        !this.organizationDetails.isHouseholdDetailsSurveyEnabled &&
        !this.organizationDetails.isFinancialHealthSurveyEnabled
      ) {
        return 'Home'
      }

      return 'HouseholdDetailsOnboarding'
    },
    handleSubmit() {
      if (this.checked === true && this.formValid) {
        this.createEmployeeAccount({
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          confirmEmail: this.confirmEmail,
          password: this.password,
          birthYear: this.birthYear || null,
          zipCode: this.zipCode,
          code: this.employeeCode.code,
          preferredLanguage: this.enableLangPref ? this.preferredLanguage : this.currentLanguage,
          enableSmsAlerts: this.enableSmsAlerts,
          enableEmailAlerts: this.enableEmailAlerts,
          enablePhoneContact: this.enablePhoneContact,
          phoneNumber: this.phoneNumber,
        })
      }
    },
  },
  computed: {
    ...mapState(['organizationDetails', 'organizationCssVars', 'currentLanguage']),
    isNotValidPassword() {
      let regex_pass = new RegExp(
        '(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[~!@#$%^&;*()_+{}":])(?=.{10,})',
      )

      return this.password.length > 0 && !regex_pass.test(this.password)
    },
    passwordLongEnough() {
      return this.password.length >= 10
    },
    passwordsMatch() {
      return this.password == this.verifiedPassword
    },
    emailsMatch() {
      return this.email === this.confirmEmail
    },
    birthYearValid() {
      const today = new Date()
      const birthYearRange = this.birthYear < today.getFullYear() && this.birthYear > 1900

      if (this.birthYear.length > 0) {
        return birthYearRange
      }

      if (
        this.birthYear.length === 0 &&
        this.employeeSchema &&
        !this.employeeSchema.hasOwnProperty('birthYear')
      ) {
        return true
      }
    },
    phoneNumberValid() {
      if (!this.enableSms) {
        return true
      }

      if (this.phoneNumber.length === 0) {
        return true
      }
      if (this.phoneNumber.length === 13) {
        return true
      }
      return false
    },
    zipCodeValid() {
      const zipRegex = new RegExp(/^[0-9]{5}?$/)

      if (this.zipCode.length === 0) {
        return true
      }

      if (
        this.zipCode.length === 0 &&
        this.employeeSchema &&
        !this.employeeSchema.hasOwnProperty('zipCode')
      ) {
        return true
      }

      const zipValid =
        this.zipCode &&
        this.zipCode.length > 1 &&
        (this.zipCode.length === 5 || this.zipCode.length === 10) &&
        !(this.errors && this.errors.zipCode) &&
        zipRegex.test(this.zipCode)

      if (!zipValid) {
        this.clientErrors = {
          ...this.clientErrors,
          zipCode: [
            this.$gettext(
              'Please enter a valid, 5-digit zip code. If your zip code is less than five digits add leading zeros (e.g. 00123)',
            ),
          ],
        }

        return false
      } else {
        this.clientErrors = { ...this.clientErrors, zipCode: [] }

        return true
      }
    },
    formValid() {
      return (
        this.checked === true &&
        this.password === this.verifiedPassword &&
        (this.enableSmsAlerts || this.enableEmailAlerts) &&
        this.preferredLanguage &&
        (this.enablePhoneContact || this.enableSmsAlerts ? this.phoneNumberValid : true) &&
        this.zipCodeValid &&
        this.birthYearValid &&
        this.birthYearValid &&
        this.passwordsMatch &&
        this.passwordLongEnough &&
        this.emailsMatch
      )
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.registration {
  @include screen-container();

  &__section {
    margin-bottom: 0.5em;
  }

  &__subtitle {
    font-size: 12px;
    font-weight: 300;
    line-height: 1rem;
  }

  &__phone-number {
    margin-top: -1em;
  }
}
.md-checkbox {
  display: flex;
}

.button-container {
  display: flex;
  flex-flow: row;
  justify-content: center;
}

.next-button {
  @include primary-button(var(--dark-brand-color));
  margin-top: 40px;
}

/* Customize the label (the checkbox-container) */
.checkbox-container {
  display: block;
  position: relative;
  padding: 1.5rem;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 25px;
  width: 25px;
  background-color: $soft-gray;
}

/* On mouse-over, add a grey background color */
.checkbox-container:hover input ~ .checkmark {
  background-color: $mid-gray;
}

/* When the checkbox is checked, add a blue background */
.checkbox-container input:checked ~ .checkmark {
  background-color: var(--dark-brand-color);
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.checkbox-container .checkmark:after {
  left: 9px;
  top: 5px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

.checkboxes {
  display: flex;
}

.ruby-font {
  color: $ruby;
}

.italics {
  font-style: italic;
}

.light {
  font-weight: 300;
}
</style>
